import { module } from "angular";
import type { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { TTLCacheObservable } from "App/Modules/Decorators";

import type {
    IAlarm,
    IAlarmCentralSite,
    IAlarmHandlingInstruction,
    IAlarmSlim,
    IConfigurationGroup,
    IConfigurationGroupCare,
    IConfigurationGroupDetection,
    IConfigurationGroupMonitoring,
    IConfigurationGroupRecording,
    IConfigurationGroupReporting,
    IConfigurationGroupWithSchedule,
    IDataPolicy,
    IDataRetention,
    IEventReport,
    IMap,
    IResponse,
    IScheduleException,
    ISite,
    ISiteEquipmentMonitoringResult,
    ISiteEquipmentSlim,
    ISiteEquipmentWithCoverages,
    ISiteMonitoring,
    ISiteMonitoringSlim,
    ISiteSlim,
    ITeamMember,
    ITeamSlim,
    SiteStatistics,
} from "App/Modules/Models";
import {
    AlarmCentralType,
    AlarmClassification,
    ConfigurationGroupType,
    ConfigurationGroupTypeFunctions,
    EquipmentClass,
    EquipmentClassFunctions,
    EquipmentFunctions,
    EquipmentTypeFunctions,
    FunctionalityType,
    MonitoringFunctions,
    ScheduleFunctions,
    SiteFunctions,
    SiteState,
    SubscriptionType,
    VideoStreamFunctions,
    ViewCoverageFunctions,
} from "App/Modules/Models";
import type { IHttpService } from "App/Modules/Services";
import type { IRolesRepository } from "../Role/IRolesRepository";
import type { ISitesRepository } from "./ISitesRepository";

export class SitesRepository implements ISitesRepository {
    public static $inject = ["mmHttpService", "irRolesRepository"];

    public static enhanceConfigurationGroup<T extends IConfigurationGroup>(siteId: string, configurationGroup: T): T {
        configurationGroup.typeName = configurationGroup.type as any as keyof typeof ConfigurationGroupType;
        configurationGroup.type = ConfigurationGroupType[configurationGroup.typeName];
        configurationGroup.icon = ConfigurationGroupTypeFunctions.configurationGroupTypeIcon(configurationGroup.type);
        configurationGroup.siteId = siteId;

        if ((configurationGroup as any).schedule) {
            ScheduleFunctions.enhanceSchedule((configurationGroup as any).schedule);
        }

        return configurationGroup;
    }

    public static enhanceSite(site: ISiteSlim | ISite): void {
        site.siteStateName = site.siteState as unknown as keyof typeof SiteState;
        site.siteState = SiteState[site.siteStateName];
        site.subscriptionTypeName = site.subscriptionType as unknown as keyof typeof SiteState;
        site.subscriptionType = SubscriptionType[site.subscriptionTypeName];
        if (site.functionalityTypes) {
            site.functionalityTypeNames = site.functionalityTypes.map(
                (ft) => ft as unknown as keyof typeof FunctionalityType,
            );
            site.functionalityTypes = site.functionalityTypeNames.map((ft) => FunctionalityType[ft]);
        }
        site.siteIcon = SiteFunctions.getSubscriptionTypeIcon(site);
    }

    public static enhanceSiteAlarmCentrals(siteAlarmCentrals: IAlarmCentralSite[]) {
        siteAlarmCentrals.forEach((acs: IAlarmCentralSite) => {
            acs.alarmCentralType = AlarmCentralType[acs.alarmCentralType as any as keyof typeof AlarmCentralType];
        });
    }

    constructor(private readonly httpService: IHttpService, private readonly rolesRepository: IRolesRepository) {}

    public EnhanceTeamMemberResponse(response: IResponse<ITeamMember>): IResponse<ITeamMember> {
        if (!response.data) {
            return response;
        }

        if (response.data.role) {
            response.data.role = this.rolesRepository.addTranslatedLabel(response.data.role);
        } else {
            response.data.role = this.rolesRepository.getNoRole();
        }
        return response;
    }

    public getSites(): Observable<IResponse<ISiteSlim[]>> {
        return this.httpService.get<ISiteSlim[]>("sites").pipe(
            map((response) => {
                response.data.forEach((site) => {
                    SitesRepository.enhanceSite(site);
                });
                return response;
            }),
        );
    }

    public getSitesStatistics(): Observable<IResponse<SiteStatistics[]>> {
        return this.httpService.get<SiteStatistics[]>("sites/siteStatistics");
    }

    @TTLCacheObservable(2000)
    public getSite(siteId: string): Observable<IResponse<ISite>> {
        return this.httpService.get<ISite>(`sites/${siteId}`).pipe(
            map((response: IResponse<ISite>) => {
                if (!response.data) {
                    return response;
                }
                SitesRepository.enhanceSite(response.data);
                SitesRepository.enhanceSiteAlarmCentrals(response.data.alarmCentrals);
                return response;
            }),
        );
    }

    public getSiteSlim(siteId: string): Observable<IResponse<ISiteSlim>> {
        return this.httpService.get<ISiteSlim>(`sites/${siteId}/slim`).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }
                SitesRepository.enhanceSite(response.data);
                return response;
            }),
        );
    }

    public getSiteWithMonitoring(siteId: string): Observable<IResponse<ISiteMonitoring>> {
        return this.httpService.get<ISiteMonitoring>(`sites/${siteId}/monitoring`).pipe(
            map((response) => {
                response.data = MonitoringFunctions.enhanceSiteMonitoring(response.data);
                return response;
            }),
        );
    }

    public getSitesWithMonitoring(): Observable<IResponse<ISiteMonitoringSlim[]>> {
        return this.httpService.get<ISiteMonitoringSlim[]>("sites/monitoring").pipe(
            map((response) => {
                response.data = response.data.map((site) => MonitoringFunctions.enhanceSiteMonitoring(site));
                return response;
            }),
        );
    }

    public getAlarmsForSite(siteId: string): Observable<IResponse<IAlarmSlim[]>> {
        return this.httpService.get<IAlarmSlim[]>(`sites/${siteId}/alarms`).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }
                response.data.map((alarm) => {
                    alarm.alarmHandling.alarmClassificationName = alarm.alarmHandling
                        .alarmClassification as unknown as keyof typeof AlarmClassification;
                    alarm.alarmHandling.alarmClassification =
                        AlarmClassification[alarm.alarmHandling.alarmClassificationName];
                    return alarm;
                });
                return response;
            }),
        );
    }

    public getAlarmsHistoryForSite(siteId: string, dtFrom: string, dtTo: string): Observable<IResponse<IAlarm[]>> {
        return this.httpService.get<IAlarm[]>(`sites/${siteId}/alarms/history`, { dtFrom, dtTo }).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }

                response.data.forEach((alarm) => {
                    alarm.alarmHandling.alarmClassificationName = alarm.alarmHandling
                        .alarmClassification as unknown as keyof typeof AlarmClassification;
                    alarm.alarmHandling.alarmClassification =
                        AlarmClassification[alarm.alarmHandling.alarmClassificationName];
                });

                return response;
            }),
        );
    }

    public getConfigurationGroups(
        siteId: string,
        onlyOfType: ConfigurationGroupType = null,
    ): Observable<IResponse<IConfigurationGroup[]>> {
        let path = `sites/${siteId}/configurations`;
        if (onlyOfType != null) {
            path = `${path}/${ConfigurationGroupType[onlyOfType]}`;
        }
        return this.httpService.get<IConfigurationGroup[]>(path).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }

                response.data.map((cg) => SitesRepository.enhanceConfigurationGroup(siteId, cg));

                return response;
            }),
        );
    }

    public getConfigurationGroupsWithSchedules(
        siteId: string,
    ): Observable<IResponse<IConfigurationGroupWithSchedule[]>> {
        const path = `sites/${siteId}/configurations/withSchedules`;
        return this.httpService.get<IConfigurationGroupWithSchedule[]>(path).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }

                response.data.map((cg) => SitesRepository.enhanceConfigurationGroup(siteId, cg));

                return response;
            }),
        );
    }

    public getHistoricalExceptions(
        siteId: string,
        configurationGroupType: keyof typeof ConfigurationGroupType,
        configurationGroupId: string,
    ): Observable<IResponse<IScheduleException[]>> {
        const path = `sites/${siteId}/configurations/${configurationGroupType}/${configurationGroupId}/schedule/exceptions`;
        return this.httpService.get<IScheduleException[]>(path);
    }

    public getConfigurationGroupCares(siteId: string): Observable<IResponse<IConfigurationGroupCare[]>> {
        const path = `sites/${siteId}/configurations/care`;
        return this.httpService.get<IConfigurationGroupCare[]>(path).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }

                response.data.map((cg) => SitesRepository.enhanceConfigurationGroup(siteId, cg));
                return response;
            }),
        );
    }

    public getConfigurationGroupCare(
        siteId: string,
        configurationGroupId: string,
    ): Observable<IResponse<IConfigurationGroupCare>> {
        const path = `sites/${siteId}/configurations/care/${configurationGroupId}`;
        return this.httpService.get<IConfigurationGroupCare>(path).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }
                SitesRepository.enhanceConfigurationGroup(siteId, response.data);

                return response;
            }),
        );
    }

    public patchConfigurationGroupCare(
        siteId: string,
        configurationGroupId: string,
        configurationGroupCarePatch: IConfigurationGroupCare,
    ): Observable<IResponse<IConfigurationGroupCare>> {
        const path = `sites/${siteId}/configurations/care/${configurationGroupId}`;
        return this.httpService.patch<IConfigurationGroupCare>(path, configurationGroupCarePatch).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }
                SitesRepository.enhanceConfigurationGroup(siteId, response.data);

                return response;
            }),
        );
    }

    public getConfigurationGroupRecording(
        siteId: string,
        configurationGroupId: string,
    ): Observable<IResponse<IConfigurationGroupRecording>> {
        const path = `sites/${siteId}/configurations/recording/${configurationGroupId}`;
        return this.httpService.get<IConfigurationGroupRecording>(path).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }
                SitesRepository.enhanceConfigurationGroup(siteId, response.data);

                return response;
            }),
        );
    }

    public patchConfigurationGroupRecording(
        siteId: string,
        configurationGroupId: string,
        configurationGroupRecordingPatch: IConfigurationGroupRecording,
    ): Observable<IResponse<IConfigurationGroupRecording>> {
        const path = `sites/${siteId}/configurations/recording/${configurationGroupId}`;
        return this.httpService.patch<IConfigurationGroupRecording>(path, configurationGroupRecordingPatch).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }
                SitesRepository.enhanceConfigurationGroup(siteId, response.data);

                return response;
            }),
        );
    }

    public getConfigurationGroupReporting(
        siteId: string,
        configurationGroupId: string,
    ): Observable<IResponse<IConfigurationGroupReporting>> {
        const path = `sites/${siteId}/configurations/reporting/${configurationGroupId}`;
        return this.httpService.get<IConfigurationGroupReporting>(path).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }
                SitesRepository.enhanceConfigurationGroup(siteId, response.data);

                return response;
            }),
        );
    }

    public patchConfigurationGroupReporting(
        siteId: string,
        configurationGroupId: string,
        configurationGroupReportingPatch: IConfigurationGroupReporting,
    ): Observable<IResponse<IConfigurationGroupReporting>> {
        const path = `sites/${siteId}/configurations/reporting/${configurationGroupId}`;
        return this.httpService.patch<IConfigurationGroupReporting>(path, configurationGroupReportingPatch).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }
                SitesRepository.enhanceConfigurationGroup(siteId, response.data);

                return response;
            }),
        );
    }

    public getConfigurationGroupDetection(
        siteId: string,
        configurationGroupId: string,
    ): Observable<IResponse<IConfigurationGroupDetection>> {
        const path = `sites/${siteId}/configurations/detection/${configurationGroupId}`;
        return this.httpService.get<IConfigurationGroupDetection>(path).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }

                SitesRepository.enhanceConfigurationGroup(siteId, response.data);

                return response;
            }),
        );
    }

    public patchConfigurationGroupDetection(
        siteId: string,
        configurationGroupId: string,
        configurationGroupDetectionPatch: IConfigurationGroupDetection,
    ): Observable<IResponse<IConfigurationGroupDetection>> {
        const path = `sites/${siteId}/configurations/detection/${configurationGroupId}`;
        return this.httpService.patch<IConfigurationGroupDetection>(path, configurationGroupDetectionPatch).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }

                SitesRepository.enhanceConfigurationGroup(siteId, response.data);

                return response;
            }),
        );
    }

    public patchConfigurationGroupWithSchedule(
        siteId: string,
        configurationGroupId: string,
        configurationGroupWithSchedulePatch: IConfigurationGroupWithSchedule,
    ): Observable<IResponse<IConfigurationGroupWithSchedule>> {
        const path = `sites/${siteId}/configurations/scheduleexceptions/${configurationGroupId}`;
        return this.httpService.patch<IConfigurationGroupWithSchedule>(path, configurationGroupWithSchedulePatch).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }

                SitesRepository.enhanceConfigurationGroup(siteId, response.data);

                return response;
            }),
        );
    }

    public getConfigurationGroupMonitoring(
        siteId: string,
        configurationGroupId: string,
    ): Observable<IResponse<IConfigurationGroupMonitoring>> {
        const path = `sites/${siteId}/configurations/monitoring/${configurationGroupId}`;
        return this.httpService.get<IConfigurationGroupMonitoring>(path).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }
                SitesRepository.enhanceConfigurationGroup(siteId, response.data);

                return response;
            }),
        );
    }

    public patchConfigurationGroupMonitoring(
        siteId: string,
        configurationGroupId: string,
        configurationGroupMonitoringPatch: IConfigurationGroupMonitoring,
    ): Observable<IResponse<IConfigurationGroupMonitoring>> {
        const path = `sites/${siteId}/configurations/monitoring/${configurationGroupId}`;
        return this.httpService.patch<IConfigurationGroupMonitoring>(path, configurationGroupMonitoringPatch).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }
                SitesRepository.enhanceConfigurationGroup(siteId, response.data);

                return response;
            }),
        );
    }

    public addConfigurationGroup(
        siteId: string,
        configurationGroup: IConfigurationGroup,
    ): Observable<IResponse<IConfigurationGroup>> {
        const path = `sites/${siteId}/configurations/`;
        return this.httpService.post<IConfigurationGroup>(path, configurationGroup).pipe(
            map((response) => {
                if (!response.data) {
                    return response;
                }
                SitesRepository.enhanceConfigurationGroup(siteId, response.data);
                return response;
            }),
        );
    }

    public deleteConfigurationGroup(siteId: string, configurationGroupId: string): Observable<IResponse<void>> {
        const path = `sites/${siteId}/configurations/${configurationGroupId}`;
        return this.httpService.delete(path);
    }

    public getEventReportsForSite(siteId: string, dtFrom: string, dtTo: string): Observable<IResponse<IEventReport[]>> {
        return this.httpService.get<IEventReport[]>(`sites/${siteId}/eventReports`, { dtFrom, dtTo });
    }

    public getMap(siteId: string): Observable<IResponse<IMap>> {
        return this.httpService.get<IMap>(`sites/${siteId}/map`);
    }

    public getSiteEquipmentsForSite(siteId: string): Observable<IResponse<ISiteEquipmentSlim[]>> {
        return this.httpService.get<ISiteEquipmentSlim[]>(`sites/${siteId}/equipment`).pipe(
            map((response: IResponse<ISiteEquipmentSlim[]>) => {
                if (!response.data) {
                    return response;
                }
                response.data.map((se) => {
                    se.class = EquipmentClass[se.class as any as keyof typeof EquipmentClass];
                    se.icon = EquipmentClassFunctions.EquipmentClassIcon(se.class);
                    if (se.type) {
                        EquipmentTypeFunctions.enhanceEquipmentType(se.type);
                    }
                    return se;
                });
                return response;
            }),
        );
    }

    public getSiteEquipments(
        siteId: string,
        onlyOfType: EquipmentClass = null,
    ): Observable<IResponse<ISiteEquipmentSlim[]>> {
        let path = `sites/${siteId}/equipment`;
        if (onlyOfType != null) {
            path = `${path}/${EquipmentClass[onlyOfType]}`;
        }
        return this.httpService.get<ISiteEquipmentSlim[]>(path).pipe(
            map((response: IResponse<ISiteEquipmentSlim[]>) => {
                if (!response.data) {
                    return response;
                }
                response.data.map((se) => {
                    se.class = EquipmentClass[se.class as any as keyof typeof EquipmentClass];
                    se.icon = EquipmentClassFunctions.EquipmentClassIcon(se.class);
                    if (se.type) {
                        EquipmentTypeFunctions.enhanceEquipmentType(se.type);
                    }
                    return se;
                });
                return response;
            }),
        );
    }

    public getSiteEquipmentsWithCoveragesForSite(siteId: string): Observable<IResponse<ISiteEquipmentWithCoverages[]>> {
        return this.httpService.get<ISiteEquipmentWithCoverages[]>(`sites/${siteId}/equipment/withCoverages`).pipe(
            map((response: IResponse<ISiteEquipmentWithCoverages[]>) => {
                if (!response.data) {
                    return response;
                }
                response.data.map((se) => {
                    if (se.equipment) {
                        EquipmentFunctions.enhanceEquipment(se.equipment);
                    }

                    se.className = se.class as any;
                    se.class = EquipmentClass[se.className];
                    se.icon = EquipmentClassFunctions.EquipmentClassIcon(se.class);
                    if (se.type) {
                        EquipmentTypeFunctions.enhanceEquipmentType(se.type);
                    }

                    if (se.videoStreams !== null && se.videoStreams.length > 0) {
                        VideoStreamFunctions.enhanceVideoStreams(se.videoStreams);
                    }

                    if (se.viewCoverages && se.viewCoverages.length > 0) {
                        se.viewCoverages.forEach((vc) => {
                            ViewCoverageFunctions.enhanceViewCoverage(vc);
                        });
                    }

                    return se;
                });
                return response;
            }),
        );
    }

    public getEquipmentMonitoringForSite(siteId: string): Observable<IResponse<ISiteEquipmentMonitoringResult[]>> {
        return this.httpService.get<ISiteEquipmentMonitoringResult[]>(`sites/${siteId}/equipment/monitoring`).pipe(
            map((response: IResponse<any>) => {
                if (!response.data) {
                    return response;
                }

                response.data.map((se: ISiteEquipmentMonitoringResult) => {
                    se.equipmentClassName = se.equipmentClass as any;
                    se.equipmentClass = EquipmentClass[se.equipmentClassName];
                    se.equipmentIcon = EquipmentClassFunctions.EquipmentClassIcon(se.equipmentClass);
                    MonitoringFunctions.enhanceHeartbeatStatus(se, se.dtLastHeartBeat);
                    VideoStreamFunctions.enhanceVideoStreams(se.videoStreams);
                    return se;
                });
                return response;
            }),
        );
    }

    public requestNewImageUpload(siteId: string): Observable<IResponse<void>> {
        return this.httpService.post<void>(`sites/${siteId}/equipment/requestImageUpload`);
    }

    public rotateCameraOnSite(siteId: string, siteEquipmentId: string, position: string): Observable<IResponse<void>> {
        return this.httpService.post<void>(`sites/${siteId}/equipment/${siteEquipmentId}/position/${position}`);
    }

    public getUsersForSite(siteId: string): Observable<IResponse<ITeamMember[]>> {
        return this.httpService.get<ITeamMember[]>(`sites/${siteId}/users`).pipe(
            map((response: IResponse<ITeamMember[]>) => {
                if (!response.data) {
                    return response;
                }

                response.data.map((user) => {
                    if (user.role) {
                        user.role = this.rolesRepository.addTranslatedLabel(user.role);
                    } else {
                        user.role = this.rolesRepository.getNoRole();
                    }
                    return user;
                });

                return response;
            }),
        );
    }

    public getInstructionsForSite(siteId: string): Observable<IResponse<IAlarmHandlingInstruction[]>> {
        return this.httpService.get<IAlarmHandlingInstruction[]>(`sites/${siteId}/instructions`);
    }

    public getTeamsForSite(siteId: string): Observable<IResponse<ITeamSlim[]>> {
        return this.httpService.get<ITeamSlim[]>(`sites/${siteId}/teams`);
    }

    public patchSite(siteId: string, data: any): Observable<IResponse<ISite>> {
        return this.httpService.patch<ISite>(`sites/${siteId}`, data).pipe(
            map((response: IResponse<ISite>) => {
                if (!response.data) {
                    return response;
                }
                SitesRepository.enhanceSite(response.data);
                SitesRepository.enhanceSiteAlarmCentrals(response.data.alarmCentrals);
                return response;
            }),
        );
    }

    public putSiteNotes(siteId: string, newNotes: string): Observable<IResponse<string>> {
        return this.httpService.put<string>(`sites/${siteId}/notes`, JSON.stringify(newNotes));
    }

    public addSite(
        site: Partial<Omit<ISite, "name">> & Pick<ISite, "name"> & { partnerId: string },
    ): Observable<IResponse<ISite>> {
        return this.httpService.post<ISite>("sites", site).pipe(
            map((response) => {
                SitesRepository.enhanceSite(response.data);
                return response;
            }),
        );
    }

    public addUserToSite(siteId: string, user: ITeamMember): Observable<IResponse<ITeamMember>> {
        return this.httpService
            .post<ITeamMember>(`sites/${siteId}/users`, user)
            .pipe(map((response: IResponse<ITeamMember>) => this.EnhanceTeamMemberResponse(response)));
    }

    public patchUser(siteId: string, partnerUserId: string, userDelta: any): Observable<IResponse<ITeamMember>> {
        return this.httpService
            .patch<ITeamMember>(`sites/${siteId}/users/${partnerUserId}`, userDelta)
            .pipe(map((response: IResponse<ITeamMember>) => this.EnhanceTeamMemberResponse(response)));
    }

    public removeUserFromSite(siteId: string, userId: string): Observable<IResponse<void>> {
        return this.httpService.delete(`sites/${siteId}/users/${userId}`);
    }

    public putMap(siteId: string, data: any): Observable<IResponse<void>> {
        return this.httpService.put<void>(`sites/${siteId}/map`, data);
    }

    public patchMap(siteId: string, siteMap: IMap): Observable<IResponse<IMap>> {
        return this.httpService.patch<IMap>(`sites/${siteId}/map`, siteMap);
    }

    public getGlobalDataRetention(siteId: string): Observable<IResponse<IDataRetention>> {
        return this.httpService.get<IDataRetention>(`sites/${siteId}/dataRetentions/global`);
    }

    public addDataRetention(siteId: string, dataRetention: IDataRetention): Observable<IResponse<IDataRetention>> {
        return this.httpService.post<IDataRetention>(`sites/${siteId}/dataRetentions`, dataRetention);
    }

    public patchDataRetention(
        siteId: string,
        dataRetentionId: string,
        dataRetention: IDataRetention,
    ): Observable<IResponse<IDataRetention>> {
        return this.httpService.patch<IDataRetention>(
            `sites/${siteId}/dataRetentions/${dataRetentionId}`,
            dataRetention,
        );
    }

    public deleteDataRetention(siteId: string, dataRetentionId: string): Observable<IResponse<void>> {
        return this.httpService.delete(`sites/${siteId}/dataRetentions/${dataRetentionId}`);
    }

    public patchDataPolicy(siteId: string, dataPolicy: IDataPolicy): Observable<IResponse<IDataPolicy>> {
        return this.httpService.patch<IDataPolicy>(`sites/${siteId}/dataPolicy`, dataPolicy);
    }
}

module("mmRepositories").service("mmSitesRepository", SitesRepository);
